/**
 * K8s node pools: list/detail/create/update/delete/scale, node lifecycle
 * (drain/remove/recover), and pool machines. Pool types live here.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApi, unwrap, k8sPath, arr, k8sKeys } from './_internal';

export interface K8sTaint { key: string; value?: string; effect: string }

export interface K8sPool {
  id: string;
  name?: string;
  description?: string;
  /** Flavor code (API field is `instanceType`). */
  instanceType?: string;
  cores?: number;
  memory?: number;
  volumes?: number;
  networks?: number;
  currentSize?: number;
  desiredSize?: number;
  minSize?: number;
  maxSize?: number;
  actualMinSize?: number;
  actualMaxSize?: number;
  totalNodes?: number;
  autoscaling?: boolean;
  autoscalingClass?: string;
  zone?: string;
  status?: string;
  // ---- Detail endpoint extras (different shape than the list row) ----
  /** Detail uses plain min/max (list uses minSize/maxSize). */
  min?: number;
  max?: number;
  /** 'worker'|'master' (string) on detail; may be array elsewhere. */
  roles?: string | string[];
  /** Node (machine) ids in this pool. */
  machines?: string[];
  image?: string;
  labels?: Record<string, string> | { key: string; value: string }[];
  tags?: Record<string, string>;
  taints?: K8sTaint[];
  metadata?: { flavor?: string; type?: string; image?: { name?: string; distro?: string }; gpu?: unknown; labels?: unknown };
  createdAt?: string;
  updatedAt?: string;
}

export function useClusterPools(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.pools(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async () => arr<K8sPool>(unwrap(await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/nodepools`))), 'nodepools'),
    // While any pool is mid-operation (resizing/provisioning/draining), poll so the
    // node count and status track the scale to completion; stop polling when idle.
    refetchInterval: (q) =>
      (q.state.data ?? []).some((p) => /updating|resizing|provision|drain|pending/i.test(p.status ?? '')) ? 5000 : false,
  });
}

/** Single node-pool detail (richer shape: roles/min/max/machines/metadata.flavor/labels). */
export function useClusterPool(serviceId: string | undefined, clusterId: string | undefined, poolId: string | undefined) {
  return useQuery({
    queryKey: ['k8s', serviceId ?? '', 'clusters', clusterId ?? '', 'pools', poolId ?? '', 'detail'] as const,
    enabled: !!serviceId && !!clusterId && !!poolId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ nodepool?: K8sPool } | K8sPool>(await serviceApi.get(k8sPath(serviceId!, `clusters/${clusterId}/nodepools/${poolId}`)));
      return (body && typeof body === 'object' && 'nodepool' in body ? body.nodepool : body) as K8sPool;
    },
  });
}

export function useUpdatePool(serviceId: string, clusterId: string, poolId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.patch(k8sPath(serviceId, `clusters/${clusterId}/nodepools/${poolId}`), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.pools(serviceId, clusterId) }); options?.onSuccess?.(...a); },
  });
}

export function useCreatePool(serviceId: string, clusterId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.post(k8sPath(serviceId, `clusters/${clusterId}/nodepools`), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.pools(serviceId, clusterId) }); options?.onSuccess?.(...a); },
  });
}

export function useDeletePool(serviceId: string, clusterId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pid: string) => (await serviceApi.delete(k8sPath(serviceId, `clusters/${clusterId}/nodepools/${pid}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.pools(serviceId, clusterId) }); options?.onSuccess?.(...a); },
  });
}

export function useScalePool(serviceId: string, clusterId: string, poolId: string, options?: UseMutationOptions<unknown, unknown, { desiredSize: number; removalStrategy?: string }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { desiredSize: number; removalStrategy?: string }) => (await serviceApi.put(k8sPath(serviceId, `clusters/${clusterId}/nodepools/${poolId}/scale`), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.pools(serviceId, clusterId) }); options?.onSuccess?.(...a); },
  });
}

// ---- Pool node lifecycle: drain / remove / recover -----------------------

export function useDrainNode(serviceId: string, clusterId: string, poolId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (node: string) => (await serviceApi.post(k8sPath(serviceId, `clusters/${clusterId}/nodepools/${poolId}/drain_node`), { nodeName: node })).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.poolMachines(serviceId, clusterId, poolId) }); options?.onSuccess?.(...a); },
  });
}

export function useRemoveNode(serviceId: string, clusterId: string, poolId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (node: string) => (await serviceApi.post(k8sPath(serviceId, `clusters/${clusterId}/nodepools/${poolId}/remove_node`), { nodeName: node })).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.poolMachines(serviceId, clusterId, poolId) }); qc.invalidateQueries({ queryKey: k8sKeys.pools(serviceId, clusterId) }); options?.onSuccess?.(...a); },
  });
}

export function useRecoverPool(serviceId: string, clusterId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (poolId: string) => (await serviceApi.post(k8sPath(serviceId, `clusters/${clusterId}/nodepools/${poolId}/recover`), {})).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: k8sKeys.pools(serviceId, clusterId) }); options?.onSuccess?.(...a); },
  });
}

// ---- Pool machines + scale ----------------------------------------------

export interface PoolMachine {
  id: string;
  name?: string;
  /** Top-level node type, e.g. `kubernetes=worker` (role lives here, not in `role`). */
  type?: string;
  /** Real field is `state` (Machine.state in legacy types). `status` kept as fallback. */
  state?: string;
  status?: string;
  /** Assigned IP is at `metadata.ip`; flat `ip` kept as fallback. */
  ip?: string;
  metadata?: { ip?: string };
  uptime?: string;
  version?: string;
  os?: string;
  runtime?: string;
}

export function usePoolMachines(serviceId: string | undefined, clusterId: string | undefined, poolId: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.poolMachines(serviceId ?? '', clusterId ?? '', poolId ?? ''),
    enabled: !!serviceId && !!clusterId && !!poolId,
    retry: false,
    queryFn: async () => arr<PoolMachine>(unwrap(await serviceApi.get(k8sPath(serviceId!, `clusters/${clusterId}/nodepools/${poolId}/machines`))), 'machines'),
  });
}
